import React from 'react';
import { money } from '../../utils/invoiceLineMath';

// The tick box under the Record Payment window's summary (D49 phase 5).
//
// An amount paid over the bills ticked, or a payment with no bills ticked at all
// (an advance), becomes a supplier credit ONLY when the person ticks this. A typo of
// an extra zero in the amount must not silently become real credit with a supplier
// that then has to be voided, and a credit that has paid down another bill cannot
// be voided at all. So nothing is held until it is ticked, and the server agrees:
// it never holds a credit unless `holdRemainderAsCredit` is exactly true.

/** Less than this over the bills is rounding, not an excess. Same figure the server uses. */
export const EXCESS_THRESHOLD = 0.01;

export interface PaymentCreditOptionProps {
  /** Amount paid minus the bills ticked. */
  excess: number;
  currency: string;
  supplierName: string;
  /** False when no bill is ticked: the whole payment would be an advance. */
  billsSelected: boolean;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

const PaymentCreditOption: React.FC<PaymentCreditOptionProps> = ({
  excess,
  currency,
  supplierName,
  billsSelected,
  checked,
  onChange,
  disabled = false,
}) => {
  if (!(excess > EXCESS_THRESHOLD)) return null;
  const amount = `${currency || 'TTD'} ${money(excess)}`;
  const who = supplierName.trim() || 'this supplier';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="payment-credit-option">
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-slate-800">
          {billsSelected ? (
            <>
              Hold the excess of <span className="font-semibold">{amount}</span> as a supplier credit with {who}.
            </>
          ) : (
            <>
              No bill is ticked. Record <span className="font-semibold">{amount}</span> as an advance, held as a supplier
              credit with {who}.
            </>
          )}{' '}
          It goes to their unpaid bills, oldest due first, and the rest to their next bill.
        </span>
      </label>
      {!checked ? (
        <p className="mt-2 text-xs text-slate-600">
          {billsSelected
            ? 'Left unticked, the excess is recorded on the payment as unallocated and no credit is made.'
            : 'Tick the box to record this payment, or tick the bills it pays.'}
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-600">
          Once this credit pays down another bill, this payment cannot be voided.
        </p>
      )}
    </div>
  );
};

export default PaymentCreditOption;
